import { useState } from 'react';
import {
  Sparkles,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileText,
  Package,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/classnames';
import type {
  InvoiceAnalysisResult,
  BoxCountResult,
  ProductVerificationResult,
  AnalysisMode,
} from '../../types/ai-analysis';

interface AIAnalysisPanelProps {
  readonly mode: AnalysisMode;
  readonly isAnalyzing: boolean;
  readonly streamingText: string;
  readonly result: InvoiceAnalysisResult | BoxCountResult | ProductVerificationResult | null;
  readonly error: string | null;
  readonly onRetry?: () => void;
}

function ConfidenceBadge({ confidence }: { readonly confidence: number }) {
  const getColor = (conf: number): string => {
    if (conf >= 0.8) return 'bg-green-100 text-green-800';
    if (conf >= 0.5) return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', getColor(confidence))}>
      {Math.round(confidence * 100)}%
    </span>
  );
}

function InvoiceResultView({ result }: { readonly result: InvoiceAnalysisResult }) {
  return (
    <div className="space-y-4">
      {/* Document Info */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <p className="text-xs text-gray-500">Document Type</p>
          <p className="font-medium capitalize">{result.documentType.replace('-', ' ')}</p>
        </div>
        {result.invoiceNumber && (
          <div>
            <p className="text-xs text-gray-500">Invoice #</p>
            <p className="font-medium">{result.invoiceNumber}</p>
          </div>
        )}
        {result.invoiceDate && (
          <div>
            <p className="text-xs text-gray-500">Date</p>
            <p className="font-medium">{result.invoiceDate}</p>
          </div>
        )}
        {result.supplierName && (
          <div>
            <p className="text-xs text-gray-500">Supplier</p>
            <p className="font-medium">{result.supplierName}</p>
          </div>
        )}
      </div>

      {/* Line Items */}
      <div>
        <h4 className="text-sm font-medium mb-2">Extracted Items ({result.items.length})</h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {result.items.map((item) => (
            <div
              key={`invoice-item-${item.productName}-${item.quantity}`}
              className="flex items-center justify-between p-3 bg-white border rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.productName}</p>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>{item.quantity} {item.unit}</span>
                  {item.unitPrice && <span>@ ${item.unitPrice.toFixed(2)}</span>}
                  {item.lotNumber && <span>Lot: {item.lotNumber}</span>}
                </div>
              </div>
              <ConfidenceBadge confidence={item.confidence} />
            </div>
          ))}
        </div>
      </div>

      {/* Totals */}
      {(result.subtotal ?? result.total) && (
        <div className="flex justify-end gap-4 pt-2 border-t">
          {result.subtotal && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Subtotal</p>
              <p className="font-medium">${result.subtotal.toFixed(2)}</p>
            </div>
          )}
          {result.tax && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Tax</p>
              <p className="font-medium">${result.tax.toFixed(2)}</p>
            </div>
          )}
          {result.total && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Total</p>
              <p className="font-semibold text-lg">${result.total.toFixed(2)}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BoxCountResultView({ result }: { readonly result: BoxCountResult }) {
  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
        <div className="text-center">
          <p className="text-3xl font-bold text-blue-600">{result.totalBoxes}</p>
          <p className="text-sm text-gray-500">Total Boxes</p>
        </div>
        <div className="text-center">
          <p className="text-3xl font-bold text-blue-600">{result.totalPallets}</p>
          <p className="text-sm text-gray-500">Pallets</p>
        </div>
      </div>

      {/* Detailed Counts */}
      <div>
        <h4 className="text-sm font-medium mb-2">Breakdown</h4>
        <div className="space-y-2">
          {result.counts.map((item) => (
            <div
              key={`count-${item.itemType}-${item.count}`}
              className="flex items-center justify-between p-3 bg-white border rounded-lg"
            >
              <div>
                <p className="font-medium">{item.itemType}</p>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold">{item.count}</span>
                <ConfidenceBadge confidence={item.confidence} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Observations */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium mb-2">Observations</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-gray-500">Condition:</span>{' '}
            <span className="capitalize">{result.observations.condition}</span>
          </div>
          <div>
            <span className="text-gray-500">Stacking:</span>{' '}
            <span className="capitalize">{result.observations.stacking}</span>
          </div>
          <div>
            <span className="text-gray-500">Labeling:</span>{' '}
            <span className="capitalize">{result.observations.labeling}</span>
          </div>
          <div>
            <span className="text-gray-500">Temperature:</span>{' '}
            <span className="capitalize">{result.observations.temperature}</span>
          </div>
        </div>
      </div>

      {/* Concerns */}
      {result.concerns.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h4 className="text-sm font-medium text-amber-800 mb-2">Concerns</h4>
          <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
            {result.concerns.map((concern) => (
              <li key={`concern-${concern.slice(0, 20)}`}>{concern}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ProductResultView({ result }: { readonly result: ProductVerificationResult }) {
  return (
    <div className="space-y-4">
      {/* Product Info */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium">{result.productName}</h4>
          <ConfidenceBadge confidence={result.confidence} />
        </div>
        {result.brand && <p className="text-sm text-gray-500">{result.brand}</p>}
      </div>

      {/* Key Details */}
      <div className="grid grid-cols-2 gap-4">
        {result.lotNumber && (
          <div className="p-3 bg-white border rounded-lg">
            <p className="text-xs text-gray-500">Lot Number</p>
            <p className="font-mono font-medium">{result.lotNumber}</p>
          </div>
        )}
        {result.expiryDate && (
          <div className="p-3 bg-white border rounded-lg">
            <p className="text-xs text-gray-500">Expiry Date</p>
            <p className="font-medium">{result.expiryDate}</p>
          </div>
        )}
        {result.sku && (
          <div className="p-3 bg-white border rounded-lg">
            <p className="text-xs text-gray-500">SKU</p>
            <p className="font-mono font-medium">{result.sku}</p>
          </div>
        )}
        {result.barcode && (
          <div className="p-3 bg-white border rounded-lg">
            <p className="text-xs text-gray-500">Barcode</p>
            <p className="font-mono font-medium">{result.barcode}</p>
          </div>
        )}
      </div>

      {/* Allergens */}
      {result.allergens.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="text-sm font-medium text-red-800 mb-2">Allergens</h4>
          <div className="flex flex-wrap gap-2">
            {result.allergens.map((allergen) => (
              <span
                key={`allergen-${allergen}`}
                className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded"
              >
                {allergen}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications */}
      {result.certifications.length > 0 && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="text-sm font-medium text-green-800 mb-2">Certifications</h4>
          <div className="flex flex-wrap gap-2">
            {result.certifications.map((cert) => (
              <span
                key={`cert-${cert}`}
                className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded"
              >
                {cert}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const MODE_ICONS = {
  invoice: FileText,
  'box-counting': Package,
  'product-verification': Tag,
};

const MODE_LABELS = {
  invoice: 'Invoice Analysis',
  'box-counting': 'Box Counting',
  'product-verification': 'Product Verification',
};

export function AIAnalysisPanel({
  mode,
  isAnalyzing,
  streamingText,
  result,
  error,
  onRetry,
}: AIAnalysisPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const Icon = MODE_ICONS[mode];

  return (
    <div className="bg-white border rounded-lg shadow-sm">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Icon className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-left">
            <h3 className="font-medium">{MODE_LABELS[mode]}</h3>
            <p className="text-sm text-gray-500">
              {(() => {
                if (isAnalyzing) return 'Analyzing...';
                if (result) return 'Complete';
                return 'Ready';
              })()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAnalyzing && <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />}
          {result && !error && <CheckCircle className="w-5 h-5 text-green-600" />}
          {error && <AlertCircle className="w-5 h-5 text-red-600" />}
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 border-t">
          {/* Streaming Text */}
          {isAnalyzing && streamingText && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">AI Processing</span>
              </div>
              <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono max-h-32 overflow-y-auto">
                {streamingText}
              </pre>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-800">Analysis Failed</span>
              </div>
              <p className="text-sm text-red-700 mb-3">{error}</p>
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Try Again
                </Button>
              )}
            </div>
          )}

          {/* Results */}
          {result && !error && (
            <>
              {mode === 'invoice' && (
                <InvoiceResultView result={result as InvoiceAnalysisResult} />
              )}
              {mode === 'box-counting' && (
                <BoxCountResultView result={result as BoxCountResult} />
              )}
              {mode === 'product-verification' && (
                <ProductResultView result={result as ProductVerificationResult} />
              )}
            </>
          )}

          {/* Empty State */}
          {!isAnalyzing && !result && !error && (
            <div className="text-center py-8 text-gray-500">
              <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Capture an image to start analysis</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
